// PopoverMenu.tsx — a small anchored dropdown: a trigger button + a floating panel that
// closes on outside click, Escape, or the caller invoking the `close` callback it hands
// to `children` (e.g. after a menu item's own action runs). Shared by ScenarioSwitcher's
// "⋯" action menu (Umbenennen/Duplizieren/Auf Standard zurücksetzen/Löschen) and
// ScenarioLibraryBar's "Laden" library picker — the only two floating menus in the app,
// so one generic implementation instead of two bespoke ones. Not a full ARIA menu widget
// (no roving tabindex/arrow-key navigation) — a short, occasionally-opened action list
// doesn't need that; focus/Escape/outside-click and `role="menu"` cover the accessible
// essentials the "-lite" drawer focus-trap elsewhere in this app also settles for.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';

export interface PopoverMenuProps {
  /** Accessible name for the trigger button (aria-label) — the visible trigger content
   * itself (icon and/or short text) is `triggerContent`, which need not be a full label
   * on its own (e.g. a bare kebab icon). */
  label: string;
  triggerContent: ComponentChildren;
  /** Defaults to the standalone-chip look ('chip'); pass 'btn' for a text-button trigger
   * (e.g. "Laden"). */
  triggerClass?: 'chip' | 'btn';
  /** Which edge of the trigger the popover hangs from — 'right' (default) keeps it from
   * overflowing past the viewport's right edge for a trigger near the right side of a
   * narrow toolbar; 'left' for a trigger near the left. */
  align?: 'left' | 'right';
  children: (close: () => void) => ComponentChildren;
}

export function PopoverMenu({ label, triggerContent, triggerClass, align = 'right', children }: PopoverMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div class="menu-anchor" ref={ref}>
      <button
        type="button"
        class={triggerClass ?? 'chip'}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => setOpen((o) => !o)}
      >
        {triggerContent}
      </button>
      {open ? (
        <div class={`menu-popover${align === 'left' ? ' menu-popover-left' : ''}`} role="menu">
          {children(() => setOpen(false))}
        </div>
      ) : null}
    </div>
  );
}
